import React from 'react';
import { BrowserRouter, Switch, Route } from 'react-router-dom';
import Header from 'layout/components/Header';
import login from 'features/auth/components/login/Login';
import ForgetPassword from 'features/auth/components/password/forgetPassword';
import register from 'features/auth/components/register/Register';
import onboarding from 'features/onboarding/components';
import profile from 'features/trainer/profile/components/profile';
import ProfileEdit from 'features/trainer/profile/components/ProfileEdit';
import Settings from 'features/trainer/settings/components/Settings';
import { Provider } from 'react-redux';
import storeConfig from 'config/ConfigStore';

import Home from 'features/home/components/home';
import Messages from 'features/messages/components/messages';
import MessagesAdd from 'features/messages/components/messagesAdd';
import Notifications from 'features/notifications/components/Notifications';
import Questionnaire from 'features/questionnaire/components/Questionnaire';
import OnboardingQuestions from 'features/OnboardingQuestions/components/OnboardingQuestions';
import Agreement from 'features/OnboardingQuestions/components/Agreement';

import ClientProfile from 'features/clientProfile/components';
import ProgramOverview from 'features/clientProfile/components/progressTab/program-overview/ProgramOverview';
import NutritionAssign from 'features/clientProfile/components/nutritionTab/NutritionAssign';
import NutritionHistory from 'features/clientProfile/components/nutritionTab/nutrition-history/NutritionHistory';
import AddNote from 'features/clientProfile/components/notesTab/AddNote';
import NoteView from 'features/clientProfile/components/notesTab/NoteView';
import EditNote from 'features/clientProfile/components/notesTab/EditNote';
import GoalAdd from 'features/clientProfile/components/Goal/GoalAdd';
import AssessmentAdd from 'features/clientProfile/components/assessmentTab/AssessmentAdd';
import AssessmentView from 'features/clientProfile/components/assessmentTab/AssessmentView';
import FirstSession from 'features/clientProfile/components/assessmentTab/FirstSession';
import ProgressCheckIn from 'features/clientProfile/components/assessmentTab/ProgressCheckIn';
import EditAssessment from 'features/clientProfile/components/assessmentTab/EditAssessment';

import WorkoutAssign from 'features/clientProfile/components/workoutTab/WorkoutAssign';
import WorkoutHistory from 'features/clientProfile/components/workoutTab/WorkoutHistory/WorkoutHistory';
import WorkoutLog from 'features/clientProfile/components/workoutTab/WorkoutLog/WorkoutLog';
import WorkoutLogDetails from 'features/clientProfile/components/workoutTab/WorkoutLog/WorkoutLogDetails';

import Workout from 'features/work-out/components';
import AuthProvider from 'features/auth/context/Auth';
import PrivateRoute from 'features/PrivateRoute/PrivateRoute';
import NoUserRoute from 'features/NoUserRoute/NoUserRoute';

import ActivationMessage from 'features/AuthActions/components/Activation/ActivationMessage';
import Actions from 'features/AuthActions/components/actions';

import Nutrition from 'features/nutrition/components';
import CreatePlanForm from 'features/nutrition/components/PlanCreate/CreatePlanForm';
import RenamePlanForm from 'features/nutrition/components/PlanCreate/RenamePlanForm';
import NutritionPlanDetails from 'features/nutrition/components/PlanDetails/NutritionPlanDetails';
import CreateMeal from 'features/nutrition/components/MealCreate/CreateMeal';
import EditMeal from 'features/nutrition/components/MealCreate/EditMeal';
import ClientAssign from 'features/nutrition/components/ClientAssign/ClientAssign';

import CreateWorkout from 'features/work-out/components/WorkoutCreate/WorkoutCreate';
import RenameWorkoutForm from 'features/work-out/components/WorkoutCreate/WorkoutRenameForm';
import WorkoutDetails from 'features/work-out/components/WorkoutDetails/WorkoutDetails';
import WorkoutClientAssign from 'features/work-out/components/WorkoutClientAssign/WorkoutClientAssign';
import CreateSeries from 'features/work-out/components/WorkoutSeries/CreateSeries';
import EditSeries from 'features/work-out/components/WorkoutSeries/EditSeries';
import SeriesDetails from 'features/work-out/components/WorkoutSeries/SeriesDetails';

import error404 from 'features/404/components/Error404';
import Terms from 'features/Terms';
import Privacy from 'features/Privacy';

import './App.scss';

const storeParams = storeConfig();

const clientProfileRoutes = [
  { path: '/clients/:client_id', component: ClientProfile },
  { path: '/clients/:client_id/progress-photos', component: ClientProfile },
  { path: '/clients/:client_id/notes/new', component: AddNote },
  { path: '/clients/:client_id/notes/:id', component: NoteView },
  { path: '/clients/:client_id/notes/:id/edit', component: EditNote },
  { path: '/clients/:client_id/goals/new', component: GoalAdd },
  { path: '/clients/:client_id/nutrition-assign', component: NutritionAssign },
  { path: '/clients/:client_id/workout-assign', component: WorkoutAssign },
  { path: '/clients/:client_id/program-overview', component: ProgramOverview },
  { path: '/clients/:client_id/add-assessment', component: AssessmentAdd },
  {
    path: '/clients/:client_id/add-assessment/first-session',
    component: FirstSession
  },
  {
    path: '/clients/:client_id/add-assessment/progress-check-in',
    component: ProgressCheckIn
  },
  { path: '/clients/:client_id/assessments/:id', component: AssessmentView },
  {
    path: '/clients/:client_id/assessments/:id/edit',
    component: EditAssessment
  },
  {
    path: '/clients/:client_id/workouts/:workout_id/logs/new',
    component: WorkoutLog
  },
  {
    path: '/clients/:client_id/workout-log/:id',
    component: WorkoutLogDetails
  },
  { path: '/clients/:client_id/workout-history', component: WorkoutHistory },
  {
    path: '/clients/:client_id/nutrition-history',
    component: NutritionHistory
  },
  { path: '/clients/:client_id/questionnaire', component: Questionnaire },
  {
    path: '/clients/:client_id/onboardingQuestions',
    component: OnboardingQuestions
  },
  {
    path: '/clients/:client_id/onboardingQuestions/agreement',
    component: Agreement
  }
];

const workoutsRoutes = [
  { path: '/workouts', component: Workout },
  { path: '/workouts/new', component: CreateWorkout },
  { path: '/workouts/:id', component: WorkoutDetails },
  { path: '/workouts/:id/client-assign', component: WorkoutClientAssign },
  { path: '/workouts/:id/workout-rename', component: RenameWorkoutForm },
  { path: '/workouts/:id/series/new', component: CreateSeries },
  { path: '/workouts/:workoutId/series/:id', component: SeriesDetails },
  { path: '/workouts/:workoutId/series/:id/edit', component: EditSeries }
];

const nutritionRoutes = [
  { path: '/nutrition', component: Nutrition },
  { path: '/nutrition/new', component: CreatePlanForm },
  { path: '/nutrition/:plan_id/rename', component: RenamePlanForm },
  { path: '/nutrition/:plan_id/meals/new', component: CreateMeal },
  { path: '/nutrition/:id', component: NutritionPlanDetails },
  { path: '/nutrition/:plan_id/meals/:id', component: EditMeal },
  { path: '/nutrition/:plan_id/client-assign', component: ClientAssign }
];

const messagesRoutes = [
  { path: '/messages', component: Messages },
  { path: '/messages/new', component: MessagesAdd }
];

class App extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      activeItem: ''
    };
  }

  setActive = value => {
    this.setState({ activeItem: value });
  };

  render() {
    return (
      <Provider store={storeParams.store}>
        <AuthProvider>
          <div className="App">
            <BrowserRouter>
              <Header activeItem={this.state.activeItem} />
              <section className="page-content">
                <Switch>
                  <NoUserRoute path="/login" component={login} />
                  <NoUserRoute
                    path="/password/forget"
                    component={ForgetPassword}
                  />
                  <NoUserRoute path="/register" component={register} />
                  <Route path="/get-started" component={ActivationMessage} />
                  <Route path="/terms" component={Terms} />
                  <Route path="/privacy-policy" component={Privacy} />
                  <Route path="/auth/action" component={Actions} />
                  <PrivateRoute
                    exact
                    path="/onboarding"
                    component={onboarding}
                  />
                  <PrivateRoute exact path="/settings" component={Settings} />
                  <PrivateRoute
                    exact
                    path="/profile/edit"
                    component={ProfileEdit}
                  />
                  <PrivateRoute exact path="/profile" component={profile} />
                  <PrivateRoute
                    exact
                    path="/"
                    component={Home}
                    setActive={() => this.setActive('home')}
                  />
                  {clientProfileRoutes.map((props, index) => (
                    <PrivateRoute
                      exact
                      key={`clients_${index}`}
                      setActive={() => this.setActive('home')}
                      {...props}
                    />
                  ))}
                  {workoutsRoutes.map((props, index) => (
                    <PrivateRoute
                      exact
                      key={`workouts_${index}`}
                      setActive={() => this.setActive('workouts')}
                      {...props}
                    />
                  ))}
                  {nutritionRoutes.map((props, index) => (
                    <PrivateRoute
                      exact
                      key={`nutrition_${index}`}
                      setActive={() => this.setActive('nutrition')}
                      {...props}
                    />
                  ))}
                  {messagesRoutes.map((props, index) => (
                    <PrivateRoute
                      exact
                      key={`messages_${index}`}
                      setActive={() => this.setActive('messages')}
                      {...props}
                    />
                  ))}
                  <PrivateRoute
                    exact
                    key="notifications"
                    path="/notifications"
                    component={Notifications}
                    setActive={() => this.setActive('notifications')}
                  />

                  <Route path="/*" component={error404} />
                </Switch>
              </section>
            </BrowserRouter>
          </div>
        </AuthProvider>
      </Provider>
    );
  }
}

export default App;
